import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useRepository } from 'hooks/auth'
import { useRootStore } from 'hooks/rootStore'
import { intercomStore } from 'store/intercomStore'
import {
  isSuppressedPath,
  resetIntercom,
  setIntercomLauncherHidden,
  startIntercom,
} from 'config/intercom'

/**
 * Drives the Intercom Messenger. Renders nothing.
 *
 * Intercom has no portal-side URL targeting, so unlike the HubSpot controller
 * this replaced, page targeting is entirely this component's job:
 * - boots the Messenger (except in E2E), before the authed/unauthed split so
 *   logged-out visitors get it too;
 * - hides the launcher on meeting routes, which HubSpot handled via its
 *   chatflow URL rules;
 * - hides it during the pre-session quiz — a full-screen dialog no route check
 *   can catch, because the URL doesn't change;
 * - shuts the session down on logout so the next user on a shared machine
 *   doesn't inherit the conversation.
 */
export const IntercomController = observer(function IntercomController() {
  const rootStore = useRootStore()
  const repository = useRepository()

  useEffect(() => {
    startIntercom()
  }, [])

  useEffect(() => {
    const listener = () => resetIntercom()
    repository.onLogout(listener)
    return () => repository.offLogout(listener)
  }, [repository])

  // Both reads happen during render so the component re-runs when either the
  // route or an active suppressor changes.
  const hidden =
    intercomStore.isSuppressed ||
    isSuppressedPath(rootStore.router.currentRoute?.path)

  useEffect(() => {
    setIntercomLauncherHidden(hidden)
  }, [hidden])

  return null
})
